import React, { useEffect, useState } from 'react';
import { ShieldCheck, Eye, EyeOff, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { authApi } from '../api/auth';
import { useApp } from '../context/AppContext';

export default function SetPasswordPage() {
  const token = new URLSearchParams(window.location.search).get('token');
  const { storeAuthData, refreshUser, setPage } = useApp();

  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(false);
  const [errorStatus, setErrorStatus] = useState<'invalid' | 'already_used' | 'expired' | null>(null);
  const [success, setSuccess] = useState(false);

  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    if (!token) {
      setErrorStatus('invalid');
      setLoading(false);
      return;
    }

    const validateToken = async () => {
      try {
        const response = await authApi.validateActivationToken(token);
        if (response.data.valid) {
          setEmail(response.data.email || '');
          setCompany(response.data.company || '');
        } else {
          setErrorStatus(response.data.reason as any || 'invalid');
        }
      } catch (err) {
        setErrorStatus('invalid');
      } finally {
        setLoading(false);
      }
    };

    validateToken();
  }, [token]);

  const criteria = [
    { id: 'length', label: 'Au moins 8 caractères', satisfied: password.length >= 8 },
    { id: 'uppercase', label: 'Au moins 1 majuscule', satisfied: /[A-Z]/.test(password) },
    { id: 'number', label: 'Au moins 1 chiffre', satisfied: /\d/.test(password) },
    { id: 'special', label: 'Au moins 1 caractère spécial (!@#$%^&*()_+-=[]{}|;:,.<>?)', satisfied: /[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(password) },
  ];

  const satisfiedCount = criteria.filter(c => c.satisfied).length;
  const passwordsMatch = password === confirmPassword && password.length > 0;
  const isFormValid = satisfiedCount === 4 && passwordsMatch;

  const getStrengthColor = () => {
    if (satisfiedCount === 0) return 'bg-gray-200';
    if (satisfiedCount === 1) return 'bg-red-500';
    if (satisfiedCount === 2) return 'bg-orange-500';
    if (satisfiedCount === 3) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const handleSubmit = async () => {
    if (!isFormValid || !token) return;

    setValidating(true);
    try {
      const response = await authApi.setPassword(token, password, confirmPassword);
      storeAuthData(response.data);
      await refreshUser();
      
      setSuccess(true);
      setTimeout(() => {
        setPage('reseller-dashboard');
      }, 1500);
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail?.[0]?.msg || err.response?.data?.detail || "Une erreur est survenue lors de la définition du mot de passe.";
      alert(errorMessage);
    } finally {
      setValidating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="flex justify-center items-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-green-600" />
        </div>
      </div>
    );
  }

  if (errorStatus) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10 text-center">
            <XCircle className="mx-auto h-12 w-12 text-red-500 mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Lien invalide</h2>
            <p className="text-gray-600 mb-6">
              {errorStatus === 'invalid' && "Ce lien d'activation n'est pas valide."}
              {errorStatus === 'already_used' && "Ce lien a déjà été utilisé. Si vous avez besoin d'aide, contactez contact@lodiss.com."}
              {errorStatus === 'expired' && "Ce lien a expiré. Veuillez contacter notre équipe à contact@lodiss.com pour recevoir un nouveau lien."}
            </p>
            <a
              href="mailto:contact@lodiss.com"
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              Contacter le support
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10 text-center">
            <CheckCircle2 className="mx-auto h-16 w-16 text-green-500 mb-4 animate-bounce" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Félicitations !</h2>
            <p className="text-gray-600 mb-6">Votre mot de passe a été défini avec succès !</p>
            <div className="flex items-center justify-center space-x-2 text-sm text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Connexion en cours...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <ShieldCheck className="w-12 h-12 text-green-600" />
        </div>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Bienvenue chez Lodiss, {company} !
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Définissez votre mot de passe pour accéder à votre espace revendeur.
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700">Email de connexion</label>
              <div className="mt-1">
                <input
                  type="email"
                  disabled
                  value={email}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50 text-gray-500 sm:text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Nouveau mot de passe</label>
              <div className="mt-1 relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-500"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Confirmer le mot de passe</label>
              <div className="mt-1 relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-500"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              {confirmPassword.length > 0 && !passwordsMatch && (
                <p className="mt-2 text-sm text-red-600">Les mots de passe ne correspondent pas.</p>
              )}
            </div>

            {/* Password Strength Indicator */}
            <div className="mt-4">
              <div className="flex justify-between items-center mb-1">
                <span className="text-xs font-medium text-gray-700">Force du mot de passe</span>
                <span className="text-xs font-medium text-gray-500">{satisfiedCount}/4 critères</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 mb-4 overflow-hidden">
                <div 
                  className={`h-2 transition-all duration-300 ${getStrengthColor()}`} 
                  style={{ width: `${(satisfiedCount / 4) * 100}%` }}
                ></div>
              </div>
              
              <ul className="space-y-1 text-sm">
                {criteria.map((criterion) => (
                  <li key={criterion.id} className="flex items-center">
                    {criterion.satisfied ? (
                      <CheckCircle2 className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                    ) : (
                      <XCircle className="w-4 h-4 text-gray-300 mr-2 flex-shrink-0" />
                    )}
                    <span className={criterion.satisfied ? 'text-gray-900' : 'text-gray-500'}>
                      {criterion.label}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <button
                onClick={handleSubmit}
                disabled={!isFormValid || validating}
                className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 ${
                  isFormValid && !validating ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-400 cursor-not-allowed'
                }`}
              >
                {validating ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  "Définir mon mot de passe et accéder à l'espace revendeur"
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
